<template>
  <v-dialog
    transition="scale-transition"
    origin="center center"
    id="go_paperless_action"
    :model-value="show"
    :width="$vuetify.display.xs ? '100vw' : '500px'"
    :min-width="$vuetify.display.xs ? '100vw' : '500px'"
    :min-height="$vuetify.display.xs ? '92vh' : 'fit-content'"
    persistent
    z-index="10000"
  >
    <v-card id="otp-card">
      <div :class="$vuetify.display.xs ? 'fixed-top-alert-small' : 'fixed-top-alert-large'">
        <AlertsComponent context="go_paperless" />
      </div>
      <template v-if="!success">
        <v-card-title class="text-center pb-0">
          <v-col cols="12" class="text-h2 text-wrap wrapText">
            {{ $t('L_go_paperless') }}
          </v-col>
        </v-card-title>
        <v-card-text class="font-weight-regular">
          <v-col cols="12" class="pa-0 mb-6">
            <span class="text-h4 font-weight-regular" v-if="goPaperlessType === 'PAPERLESS_COMM'">
              {{ $t('L_go_paperless_top_text') }}
            </span>
            <span
              class="text-h4 font-weight-regular"
              v-else-if="goPaperlessType === 'PAPERLESS_COMM_INVESTMENT'"
            >
              {{ $t('L_go_paperless_top_text_mutual_fund_investment') }}
            </span>
          </v-col>
          <v-col class="bg-light-grey pl-3 pr-3 pt-4 pb-0 mb-6">
            <div>
              <v-checkbox v-model="checked" class="mb-0 pb-0">
                <template v-slot:label>
                  <div class="ml-4">
                    <strong>{{ $t('L_go_paperless_yes') }}</strong>
                    {{ $t('L_go_paperless_check_text', { email: customerProfile.email }) }}
                  </div>
                </template>
              </v-checkbox>
            </div>
          </v-col>
          <div class="html-content justify-center d-flex iosIframeScroller">
            <iframe
              width="100%"
              id="edelivery_disclosure_iframe"
              v-if="htmlSrc"
              :src="htmlSrc"
              title="e-delivery disclosure html"
            />
          </div>
        </v-card-text>
      </template>
      <template v-if="success">
        <v-card-title class="text-center font-weight-regular font-size--24 pt-4">
          <v-col cols="12">
            {{ $t('L_go_paperless_thanks') }}
          </v-col>
        </v-card-title>
        <v-card-text>
          <div v-if="isEcommInvestment" class="text-center font-weight-medium">
            <p>{{ $t('L_go_paperless_chosen_investment') }}</p>
          </div>
          <div v-else class="text-center font-weight-medium">
            <p>{{ $t('L_go_paperless_chosen') }}</p>
          </div>
          <div>
            <p class="text-left mt-6">{{ $t('L_go_paperless_changes') }}</p>
            <p class="text-left mt-6">{{ $t('L_go_paperless_not_all') }}</p>
          </div>
        </v-card-text>
        <v-card-actions class="pa-4 d-flex justify-center">
          <OnexButton block size="large" color="primary" @click="close">
            {{ $t('L_go_paperless_ok') }}
          </OnexButton>
        </v-card-actions>
      </template>
      <div v-if="!success" class="d-flex justify-center flex-column mb-6">
        <OnexButton color="primary" class="btn-agree" :disabled="!checked" @click="agree">
          {{ $t('L_go_paperless_i_agree') }}
        </OnexButton>
        <div class="mt-4 text-center">
          <OnexButton
            v-if="goPaperlessType === 'PAPERLESS_COMM'"
            @click="snoozeEcommOption"
            :text="$t('L_go_paperless_skip')"
            variant="text"
            color="anchor"
          ></OnexButton>
          <OnexButton
            v-if="goPaperlessType === 'PAPERLESS_COMM_INVESTMENT'"
            @click="dismissAlertOption"
            :text="$t('L_go_paperless_skip')"
            variant="text"
            color="anchor"
          ></OnexButton>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import bff from '@/shared/api/bff';
import AlertsComponent from '../AlertsComponent.vue';
import { mapState, mapActions } from 'pinia';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { useGlobalStore } from '@/stores/global/global.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { type ComponentOptions, defineComponent } from 'vue';
import type { IGlobalStoreAlertsAndNotificationItem } from '@/stores/global/globalStore.model';
import { OnexButton } from '@primerica/onex-ui-lib';

const componentOptions: ComponentOptions = {
  name: 'GoPaperlessAction',
  components: { AlertsComponent, OnexButton },
  props: {
    // show: {
    //   type: Boolean,
    //   required: true,
    //   default: false,
    // },
    alertContext: {
      type: String,
      required: false,
      default: 'go_paperless',
    },
    // policies: {
    //   type: Array,
    //   required: true,
    // },
  },
  data: () => ({
    text: undefined,
    checked: false,
    preferences: {},
    success: false,
    processed: false,
    disablePaperlessOkButton: false,
  }),
  computed: {
    ...mapState(usePolicyStore, ['policies']),
    ...mapState(useCustomerStore, ['customerProfile']),
    ...mapState(useGlobalStore, {
      containsFeature: 'containsFeature',
      features: 'features',
      locale: 'getLocale',
      showGoPaperlessWrapper: 'showGoPaperlessWrapper',
      goPaperlessType: 'goPaperlessType',
      alertsAndNotifications: 'alertsAndNotifications',
    }),
    htmlSrc() {
      if (this.locale === 'en_CA') {
        return window.appConfig.ecomm.en_CA.ecommunication_consent;
      } else if (this.locale === 'fr_CA') {
        return window.appConfig.ecomm.fr_CA.ecommunication_consent;
      }
      return window.appConfig.ecomm.en_US.ecommunication_consent;
    },
    show() {
      const isECOMMEligible = this.containsFeature('ECOMM');
      return isECOMMEligible && this.showGoPaperlessWrapper;
    },
    isEcommInvestment() {
      return this.goPaperlessType === 'PAPERLESS_COMM_INVESTMENT';
    },
  },
  methods: {
    ...mapActions(useGlobalStore, [
      'snoozeEcomm',
      'dismissAlert',
      'setShowGoPaperlessWrapper',
      'setGoPaperlessType',
      'completeAlert',
      'addError',
      'removeAlerts',
      'addAlert',
    ]),
    async agree() {
      this.disablePaperlessOkButton = true;
      if (!this.processed) {
        this.processed = true;
        try {
          if (this.goPaperlessType === 'PAPERLESS_COMM_INVESTMENT') {
            const reqPayloadObj = {
              investment: { channel: 'DIGITAL' },
              source: 'AL',
            };
            await bff.updateInvestmentPaperless(reqPayloadObj, this.locale);
            await this.completeAlert({ alertId: this.getAlertId(), payload: {} });
          } else {
            const preferences = await bff.getCommunicationsPreferences();
            preferences.forEach((preference: { policyNumber: string }) => {
              this.preferences[preference.policyNumber] = 'DIGITAL';
            });
            await bff.updatePaperless(this.preferences, this.locale, true, 'AL');
          }
          this.addAlert({
            type: 'success',
            details: { key: 'L_go_paperless_success' },
            context: 'go_paperless',
          });
          this.$emit('success');
          this.success = true;
          this.processed = false;
        } catch (e) {
          this.disablePaperlessOkButton = false;
          this.success = false;
          this.processed = false;
          this.addError({ error: e, context: 'go_paperless' }, { root: true });
        }
      }
    },
    snoozeEcommOption() {
      if (!this.processed) {
        this.processed = true;
        this.snoozeEcomm()
          .then(() => {
            this.$emit('snooze');
          })
          .catch((e: unknown) => {
            this.addError({ error: e, context: 'go_paperless' });
          })
          .finally(() => {
            this.setShowGoPaperlessWrapper(false);
            this.setGoPaperlessType('');
          });
        this.processed = false;
      }
      // this.setShowGoPaperlessWrapper(false);
      // this.setGoPaperlessType('');
      this.close();
    },
    dismissAlertOption() {
      if (!this.processed) {
        this.processed = true;
        this.dismissAlert(this.getAlertId())
          .then(() => {
            this.$emit('snooze');
          })
          .catch((e: unknown) => {
            this.addError({ error: e, context: 'go_paperless' });
          })
          .finally(() => {
            this.setShowGoPaperlessWrapper(false);
            this.setGoPaperlessType('');
          });
        this.processed = false;
      }
      this.setShowGoPaperlessWrapper(false);
      this.setGoPaperlessType('');
    },
    close() {
      this.success = false;
      this.removeAlerts('go_paperless');
      this.checked = false;
      this.preferences = {};
      this.success = false;
      this.disablePaperlessOkButton = false;
      this.setShowGoPaperlessWrapper(false);
      this.setGoPaperlessType('');
    },
    getAlertId() {
      const alert = (this.alertsAndNotifications as IGlobalStoreAlertsAndNotificationItem[]).find(
        ({ actionType }) => actionType === 'PAPERLESS_COMM_INVESTMENT'
      );
      const alertId = String(alert?.alertId ?? '-1');
      return alertId;
    },
  },
};
export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
#go_paperless_action {
  margin-bottom: unset;
  .btn-agree {
    width: 200px;
    margin: 0 auto;
  }
}
.iosIframeScroller {
  -webkit-overflow-scrolling: touch !important;
  // overflow-y: scroll !important;
  iframe {
    border: thin solid rgba(0, 0, 0, 0.25);
    height: 200px;
  }
}
</style>
